import dayjs from '~/timeInit';

import { rangeOverlap } from '~/services/TimeService';

import type { ResolvedSurchargeBlock, SalesforceSurchargePeriod } from '~/types/surcharges';

import { daysOfWeek } from './daysOfWeek';
// `toExclusiveEnd` / `toInclusiveEnd` carry the rationale for why we operate
// in half-open space internally even though both SF input and admin storage
// use inclusive end_date — see surchargeBoundaries.ts.
import { toExclusiveEnd, toInclusiveEnd } from './surchargeBoundaries';

function resolvePriceForActive(active: Array<SalesforceSurchargePeriod>): {
  contributingIds: Array<string>;
  cost_price: number;
  sell_price: number;
} {
  const accumulating = active.filter((s) => s.accumulate);
  const nonAccumulating = active.filter((s) => !s.accumulate);

  const sumCost = accumulating.reduce((acc, s) => acc + s.cost_price, 0);
  const sumSell = accumulating.reduce((acc, s) => acc + s.sell_price, 0);
  const maxNonAccCost = nonAccumulating.length ? Math.max(...nonAccumulating.map((s) => s.cost_price)) : 0;
  const maxNonAccSell = nonAccumulating.length ? Math.max(...nonAccumulating.map((s) => s.sell_price)) : 0;

  // Which surcharges actually set this price. Under the non-accumulating `max` rule the
  // losers are still active over these dates but contribute nothing — crediting them would
  // attribute another surcharge's money to them. Ties credit both, since both produce it.
  const creditFor = (sum: number, maxNonAcc: number, read: (s: SalesforceSurchargePeriod) => number) => {
    const ids: Array<string> = [];
    if (accumulating.length > 0 && sum >= maxNonAcc) {
      ids.push(...accumulating.map((s) => s.id));
    }
    if (nonAccumulating.length > 0 && maxNonAcc >= sum) {
      ids.push(...nonAccumulating.filter((s) => read(s) === maxNonAcc).map((s) => s.id));
    }
    return ids;
  };

  return {
    // Cost and sell are maximised independently, so each can be won by a different
    // surcharge; a block is attributed to the union of both winners.
    contributingIds: [
      ...new Set([
        ...creditFor(sumCost, maxNonAccCost, (s) => s.cost_price),
        ...creditFor(sumSell, maxNonAccSell, (s) => s.sell_price),
      ]),
    ].sort(),
    cost_price: Math.max(sumCost, maxNonAccCost),
    sell_price: Math.max(sumSell, maxNonAccSell),
  };
}

// Boundaries are points in half-open space: surcharge.start_date and the day
// AFTER surcharge.end_date (since end_date is inclusive on the input).
function collectBoundaries(surcharges: Array<SalesforceSurchargePeriod>): Array<string> {
  return [...new Set(surcharges.flatMap((s) => [s.start_date, toExclusiveEnd(s.end_date)]))].sort(
    (a, b) => dayjs(a).valueOf() - dayjs(b).valueOf(),
  );
}

function mergeAdjacentBlocks(blocks: Array<ResolvedSurchargeBlock>): Array<ResolvedSurchargeBlock> {
  if (blocks.length <= 1) {
    return blocks;
  }

  const sorted = [...blocks].sort((a, b) => dayjs(a.start_date).valueOf() - dayjs(b.start_date).valueOf());
  const merged: Array<ResolvedSurchargeBlock> = [];

  const sameWeekdays = (a: ResolvedSurchargeBlock, b: ResolvedSurchargeBlock) =>
    daysOfWeek.every((d) => a[d.key] === b[d.key]);

  // sourceIds are pre-sorted, so a linear comparison is sufficient (avoids JSON.stringify overhead)
  const sameSources = (a: Array<string>, b: Array<string>) => a.length === b.length && a.every((id, i) => id === b[i]);

  for (const block of sorted) {
    const prev = merged[merged.length - 1];
    // end_date is inclusive, so two blocks are adjacent when prev.end_date + 1 === block.start_date.
    if (
      prev &&
      dayjs(prev.end_date).add(1, 'day').isSame(dayjs(block.start_date), 'day') &&
      prev.currency_code === block.currency_code &&
      prev.cost_price === block.cost_price &&
      prev.sell_price === block.sell_price &&
      prev.isMerged === block.isMerged &&
      sameWeekdays(prev, block) &&
      sameSources(prev.sourceIds, block.sourceIds)
    ) {
      prev.end_date = block.end_date;
    } else {
      merged.push({ ...block, contributingIds: [...block.contributingIds], sourceIds: [...block.sourceIds] });
    }
  }

  return merged;
}

/**
 * Splits selected SF surcharges into non-overlapping blocks with pricing:
 * final = max(sum of accumulating, max of non-accumulating) per cost and sell.
 *
 * Surcharges are grouped by currency_code first so that prices from different
 * currencies are never summed or compared against each other.
 *
 * NOTE: All start_date / end_date values (input and output) are INCLUSIVE.
 * The boundary algorithm internally works in half-open space; see comment at
 * the top of this file.
 */
export function resolveSurchargeOverlaps(surcharges: Array<SalesforceSurchargePeriod>): Array<ResolvedSurchargeBlock> {
  if (surcharges.length === 0) {
    return [];
  }

  // Group by currency and resolve each group independently
  const byCurrency = surcharges.reduce((acc, s) => {
    acc.set(s.currency_code, [...(acc.get(s.currency_code) ?? []), s]);
    return acc;
  }, new Map<string, Array<SalesforceSurchargePeriod>>());

  const allBlocks: Array<ResolvedSurchargeBlock> = [];
  for (const [currencyCode, group] of byCurrency) {
    allBlocks.push(...resolveOverlapsForCurrency(group, currencyCode));
  }

  return allBlocks;
}

function resolveOverlapsForCurrency(
  surcharges: Array<SalesforceSurchargePeriod>,
  currencyCode: string,
): Array<ResolvedSurchargeBlock> {
  if (surcharges.length === 1) {
    const surcharge = surcharges[0];
    // svc-reservation's bulkRecreate disjointness check keys on date × DoW; a
    // block with no DoW flags set is contractually meaningless (applies on no
    // days) and would persist as dead data. Drop it.
    if (!daysOfWeek.some((d) => surcharge[d.key])) {
      return [];
    }
    return [
      {
        contributingIds: [surcharge.id],
        sourceIds: [surcharge.id],
        start_date: surcharge.start_date,
        end_date: surcharge.end_date,
        monday: surcharge.monday,
        tuesday: surcharge.tuesday,
        wednesday: surcharge.wednesday,
        thursday: surcharge.thursday,
        friday: surcharge.friday,
        saturday: surcharge.saturday,
        sunday: surcharge.sunday,
        cost_price: surcharge.cost_price,
        sell_price: surcharge.sell_price,
        currency_code: currencyCode,
        isMerged: false,
      },
    ];
  }

  const boundaries = collectBoundaries(surcharges);
  const rawBlocks: Array<ResolvedSurchargeBlock> = [];

  // Each segment [segStart, segEnd) is half-open in our internal math; we emit
  // it as the inclusive range [segStart, segEnd - 1 day] since downstream
  // (admin's surcharge_date_block) treats end_date as inclusive.
  for (let i = 0; i < boundaries.length - 1; i++) {
    const segStart = boundaries[i];
    const segEnd = boundaries[i + 1];

    if (!dayjs(segStart).isBefore(dayjs(segEnd), 'day')) {
      continue;
    }

    type DayResolution = {
      contributingIds: Array<string>;
      cost_price: number;
      sell_price: number;
      sourceIds: Array<string>;
    };

    const byDay: Partial<Record<(typeof daysOfWeek)[number]['key'], DayResolution>> = {};

    for (const { key: dayKey } of daysOfWeek) {
      // Compare in half-open space against each surcharge's exclusive end
      // (end_date + 1 day) so a surcharge whose last day is segStart is still
      // considered active in this segment.
      const active = surcharges.filter(
        (s) => rangeOverlap(segStart, segEnd, s.start_date, toExclusiveEnd(s.end_date)) && s[dayKey],
      );
      if (active.length === 0) {
        continue;
      }

      const { contributingIds, cost_price, sell_price } = resolvePriceForActive(active);
      const sourceIds = [...new Set(active.map((s) => s.id))].sort();
      byDay[dayKey] = { contributingIds, cost_price, sell_price, sourceIds };
    }

    const dayKeysWithData = daysOfWeek.map((d) => d.key).filter((k) => byDay[k]);

    // Cluster days by resolved price only — days with identical cost/sell prices
    // are grouped into one block even if they come from different source surcharges.
    // sourceIds are unioned across all days in the cluster, so isMerged and sourceIds
    // reflect the combined set of contributors, not per-day attribution.
    const clusters = new Map<string, Array<string>>();

    for (const dk of dayKeysWithData) {
      const res = byDay[dk];
      const clusterKey = `${res.cost_price}|${res.sell_price}`;
      const existing = clusters.get(clusterKey);
      if (existing) {
        existing.push(dk);
      } else {
        clusters.set(clusterKey, [dk]);
      }
    }

    for (const dayKeyList of clusters.values()) {
      const firstDay = dayKeyList[0];
      const res = byDay[firstDay];
      const sourceIds = [...new Set(dayKeyList.flatMap((dk) => byDay[dk].sourceIds))].sort();
      const contributingIds = [...new Set(dayKeyList.flatMap((dk) => byDay[dk].contributingIds))].sort();
      const isMerged = sourceIds.length > 1;

      rawBlocks.push({
        contributingIds,
        sourceIds,
        start_date: segStart,
        end_date: toInclusiveEnd(segEnd),
        monday: dayKeyList.includes('monday'),
        tuesday: dayKeyList.includes('tuesday'),
        wednesday: dayKeyList.includes('wednesday'),
        thursday: dayKeyList.includes('thursday'),
        friday: dayKeyList.includes('friday'),
        saturday: dayKeyList.includes('saturday'),
        sunday: dayKeyList.includes('sunday'),
        cost_price: res.cost_price,
        sell_price: res.sell_price,
        currency_code: currencyCode,
        isMerged,
      });
    }
  }

  return mergeAdjacentBlocks(rawBlocks);
}
